import './Card.scss'

import {HeadingMods, Theme} from '../Theme'
import {ImageType, Mod} from '../../types'
import React, {Fragment, useState} from 'react'

import {Background} from '@codeurs/front'
import {Link} from '../../util/link'
import placeholder from 'assets/images/placeholder.png'
import {style} from '../../util/style'

const styles = style('card', ['image', 'content', 'title', 'text', 'link'])

export type CardType = {
	url?: string
	image?: ImageType
	title?: string
	text?: string
	linklabel?: string
	date?: string
}

export type CardLayoutType = 'event' | 'page' | 'product' | 'project'

export const Card: React.FC<{
	className?: string
	layout?: CardLayoutType
}> = ({className, layout = 'page', children}) => (
	<div className={styles().with(className).mod(layout)}>{children}</div>
)

export const CardImage: React.FC<{
	image: ImageType
	url?: string
	title?: string
}> = ({image, url, title, children}) => {
	const ImageTag = url ? Link : 'div'
	const imageProps = url ? {to: url} : {}
	const [isLoaded, setLoaded] = useState(false)

	return (
		<ImageTag
			{...imageProps}
			title={title}
			className={styles.image().is({loaded: isLoaded})}
		>
			<Background
				img={image || placeholder}
				className={styles.image('bg').toString()}
				onload={() => setLoaded(true)}
			/>
			{children}
		</ImageTag>
	)
}

export const CardImageOverlay: React.FC = () => (
	<span className={styles.image('overlay')} />
)

export const CardImageLabel: React.FC = ({children}) => (
	<span className={styles.image('label')}>{children}</span>
)

export const CardContent: React.FC = ({children}) => (
	<div className={styles.content()} data-gt-block>
		{children}
	</div>
)

export const CardTitle: React.FC<{
	theme?: typeof Theme.H3
	as?: 'h3' | 'h4' | 'h5'
	className?: string
	mod?: Mod<HeadingMods>
	title: string
	url?: string
}> = ({theme: ThemeTag = Theme.H3, as = 'h3', className, mod, title, url}) => {
	if (!title) return null
	const LinkTag = url ? Link : Fragment
	const linkProps = url ? {to: url} : {}

	return (
		<ThemeTag as={as} className={styles.title().with(className)} mod={mod}>
			<LinkTag {...linkProps}>{title}</LinkTag>
		</ThemeTag>
	)
}

export const CardText: React.FC<{text: string}> = ({text}) =>
	text && (
		<div className={styles.text()}>
			<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
		</div>
	)

export const CardLink: React.FC<{
	url: string
	description?: string
	className?: string
	hideIcon?: boolean
}> = ({url, description, className, hideIcon = false, ...props}) => {
	if (!url || !description) return null

	return (
		<Theme.Link
			to={url}
			iconafter={hideIcon ? null : 'chevronRight'}
			className={styles.link().with(className)}
			{...props}
		>
			{description}
		</Theme.Link>
	)
}
